import { MarketingSchema } from "@/schemas/marketing";
import {
    Activity,
    ArrowRight,
    PiggyBank,
    ServerCog,
    ShieldCheck,
    type LucideIcon,
} from "lucide-react";

// bigbell.ai and bigbell.ai/coin share a domain, so landing pages are linked
// by root-relative path (outside the Next.js /coin basePath).
type Provider = "aws" | "azure" | "gcp";

const LANDING_PAGES: Record<Provider, string> = {
    aws: "/lp/aws",
    azure: "/lp/azure",
    gcp: "/lp/gcp",
};

const PROVIDER_GRADIENTS: Record<Provider, string> = {
    aws: "from-[#232F3E] to-[#FA8300]",
    azure: "from-[#0B3D91] to-[#0078D4]",
    gcp: "from-[#1A73E8] to-[#34A853]",
};

const ItemsWrapper = ({ children }: { children: React.ReactNode }) => (
    <section className="not-xl:flex not-xl:flex-wrap not-xl:w-screen gap-4 mt-4">
        {children}
    </section>
);

type ItemProps = {
    provider: Provider;
    icon: LucideIcon;
    title: string;
    description: string;
};

const Item = ({ provider, icon: Icon, title, description }: ItemProps) => (
    <a href={LANDING_PAGES[provider]} className="not-sm:mx-auto group">
        <div className="xl:mt-4 flex-col border border-gray-200 rounded-md p-4 w-xs xl:w-3xs 2xl:w-xs transition-shadow group-hover:shadow-md group-hover:border-[#FA8300]">
            <div
                className={`w-full h-28 rounded-md bg-gradient-to-br ${PROVIDER_GRADIENTS[provider]} flex items-center justify-center`}
            >
                <Icon className="w-12 h-12 text-white" aria-hidden="true" />
            </div>
            <h3 className="text-md not-xl:text-sm xl:text-xs 2xl:text-sm font-semibold mt-2 leading-tight mb-1">
                {title}
            </h3>
            <p className="text-xs not-xl:text-2xs xl:text-3xs 2xl:text-2xs text-gray-2">
                {description}
            </p>
            <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#FA8300]">
                Learn more
                <ArrowRight
                    className="w-3 h-3 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                />
            </span>
        </div>
    </a>
);

const AWSMarketing = () => (
    <ItemsWrapper>
        <Item
            provider="aws"
            icon={Activity}
            title="AWS Cloud Monitoring"
            description="Track EC2, RDS and other AWS resources in real time and get instant alerts when performance spikes or anomalies appear."
        />
        <Item
            provider="aws"
            icon={ServerCog}
            title="Managed AWS Operations"
            description="Automate start/stop schedules, backups and resource lifecycles, and run your AWS infrastructure from Slack or Teams with ChatOps."
        />
        <Item
            provider="aws"
            icon={PiggyBank}
            title="AWS Cost Optimization & Consulting"
            description="See exactly which instances, databases and tags drive your monthly spend, and get a free cloud cost review from our experts."
        />
        <Item
            provider="aws"
            icon={ShieldCheck}
            title="AWS Security Scanning"
            description="Catch security issues between VAPT cycles by continuously scanning for open ports, missing MFA and misconfigurations."
        />
    </ItemsWrapper>
);

const AzureMarketing = () => (
    <ItemsWrapper>
        <Item
            provider="azure"
            icon={Activity}
            title="Azure Cloud Monitoring"
            description="Track Azure VMs and App Services in real time and get instant alerts when performance spikes or anomalies appear."
        />
        <Item
            provider="azure"
            icon={ServerCog}
            title="Managed Azure Operations"
            description="Schedule VM snapshots and state changes, and manage your Azure resources from Slack or Teams with ChatOps."
        />
        <Item
            provider="azure"
            icon={PiggyBank}
            title="Azure Cost Optimization & Consulting"
            description="See exactly which VMs, SQL databases and resource groups drive your monthly spend, and get a free cloud cost review from our experts."
        />
        <Item
            provider="azure"
            icon={ShieldCheck}
            title="Azure Security Scanning"
            description="Continuously scan for risky Network Security Groups and policies to catch security issues between VAPT cycles."
        />
    </ItemsWrapper>
);

const GCPMarketing = () => (
    <ItemsWrapper>
        <Item
            provider="gcp"
            icon={Activity}
            title="GCP Cloud Monitoring"
            description="Track Compute Engine, GKE, Cloud SQL, storage and network signals and get actionable alerts when performance deviates."
        />
        <Item
            provider="gcp"
            icon={ServerCog}
            title="Managed GCP Operations"
            description="Schedule VM state changes, snapshots and retention policies, and trigger approved Google Cloud actions with ChatOps."
        />
        <Item
            provider="gcp"
            icon={PiggyBank}
            title="GCP Cost Optimization & Consulting"
            description="Find idle VMs, oversized machine types and expensive projects, and get a free cloud cost review from our experts."
        />
        <Item
            provider="gcp"
            icon={ShieldCheck}
            title="GCP Security Scanning"
            description="Continuously surface public buckets, permissive firewall rules, exposed services and IAM configuration risks."
        />
    </ItemsWrapper>
);

type MarketingWrapperProps = {
    instanceType: string;
    marketingData?: MarketingSchema;
    children: React.ReactNode;
};

export default function MarketingWrapper({
    instanceType,
    children,
}: MarketingWrapperProps) {
    if (process.env.NEXT_PUBLIC_REMOVE_ADVERTS === "1") return children;

    let node: React.ReactNode;
    switch (instanceType) {
        case "ec2-flex":
        case "ec2-gpu":
        case "ec2-other":
        case "rds":
        case "opensearch":
        case "redshift":
        case "elasticache":
            node = <AWSMarketing />;
            break;
        case "azure":
            node = <AzureMarketing />;
            break;
        case "gcp":
            node = <GCPMarketing />;
            break;
        default:
            // This should fail the build
            throw new Error(`Unknown instance type: ${instanceType}`);
    }

    return (
        <>
            <div className="w-full" data-wrapper="true">
                <div className="xl:flex gap-4 mx-auto w-max">
                    <div className="flex-col">{children}</div>
                    <div className="flex-col">{node}</div>
                </div>
            </div>
        </>
    );
}
